import { useEffect, useRef, useCallback, useState } from "react";

export interface MidiCallbacks {
  onNoteOn?: (note: number, velocity: number) => void;
  onNoteOff?: (note: number) => void;
}

export interface MidiState {
  supported: boolean;
  connected: boolean;
  deviceName: string | null;
}

// MIDI note number to note name mapping
const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

export function midiNoteToName(noteNumber: number): { note: string; octave: number } {
  const octave = Math.floor(noteNumber / 12) - 1;
  const noteIndex = noteNumber % 12;
  return { note: NOTE_NAMES[noteIndex], octave };
}

export function midiNoteToFreq(noteNumber: number): number {
  return 440 * Math.pow(2, (noteNumber - 69) / 12);
}

// Standard General MIDI drum map
export const GM_DRUM_MAP: Record<number, string> = {
  35: "kick",      // Acoustic Bass Drum
  36: "kick",      // Bass Drum 1
  37: "rimshot",   // Side Stick
  38: "snare",     // Acoustic Snare
  39: "clap",      // Hand Clap
  40: "snare",     // Electric Snare
  41: "tom3",      // Low Floor Tom
  42: "closedhat", // Closed Hi-Hat
  43: "tom3",      // High Floor Tom
  44: "closedhat", // Pedal Hi-Hat
  45: "tom2",      // Low Tom
  46: "openhat",   // Open Hi-Hat
  47: "tom2",      // Low-Mid Tom
  48: "tom1",      // Hi-Mid Tom
  49: "cymbal",    // Crash Cymbal 1
  50: "tom1",      // High Tom
  51: "cymbal",    // Ride Cymbal 1
  52: "cymbal",    // Chinese Cymbal
  53: "cowbell",   // Ride Bell
  54: "shake",     // Tambourine
  56: "cowbell",   // Cowbell
  57: "cymbal",    // Crash Cymbal 2
  60: "conga",     // Hi Bongo
  61: "conga",     // Low Bongo
  62: "conga",     // Mute Hi Conga
  63: "conga",     // Open Hi Conga
  64: "conga",     // Low Conga
  70: "maracas",   // Maracas
  75: "clave",     // Claves
};

export function useMidi(callbacks: MidiCallbacks) {
  const callbacksRef = useRef(callbacks);
  const isMountedRef = useRef(true); // Track if component is still mounted
  const [state, setState] = useState<MidiState>({
    supported: typeof navigator !== "undefined" && "requestMIDIAccess" in navigator,
    connected: false,
    deviceName: null,
  });
  const midiAccessRef = useRef<WebMidi.MIDIAccess | null>(null);
  const connectionTimeRef = useRef<number>(0); // Track when MIDI connected

  // Keep callbacks ref updated
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  // Track mount state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleMidiMessage = useCallback((event: WebMidi.MIDIMessageEvent) => {
    // Ignore if component unmounted
    if (!isMountedRef.current) return;

    // Ignore messages for 500ms after connection (spurious init messages)
    if (Date.now() - connectionTimeRef.current < 500) return;

    const data = event.data;
    if (!data || data.length < 3) return;

    const status = data[0];
    const note = data[1];
    const velocity = data[2];
    const command = status & 0xf0;

    // Note On (0x90) with velocity > 0
    if (command === 0x90 && velocity > 0) {
      callbacksRef.current.onNoteOn?.(note, velocity);
    }
    // Note Off (0x80) or Note On with velocity 0
    else if (command === 0x80 || (command === 0x90 && velocity === 0)) {
      callbacksRef.current.onNoteOff?.(note);
    }
  }, []);

  useEffect(() => {
    if (!state.supported) return;

    const connectInputs = (access: WebMidi.MIDIAccess) => {
      let firstConnected: string | null = null;
      let hasInputs = false;

      // Use Array.from to iterate over Map values
      const inputs = Array.from(access.inputs.values());

      for (const input of inputs) {
        hasInputs = true;
        input.onmidimessage = handleMidiMessage;
        if (!firstConnected && input.state === "connected") {
          firstConnected = input.name || "MIDI Device";
        }
      }

      // Record connection time to ignore spurious messages
      connectionTimeRef.current = Date.now();

      setState((s) => ({
        ...s,
        connected: hasInputs && firstConnected !== null,
        deviceName: firstConnected,
      }));
    };

    navigator.requestMIDIAccess({ sysex: false })
      .then((access) => {
        midiAccessRef.current = access;
        connectInputs(access);

        // Handle device connect/disconnect
        access.onstatechange = () => {
          connectInputs(access);
        };
      })
      .catch(() => {
        // Web MIDI not supported or permission denied
        // This is expected on Firefox (needs extension) and some other browsers
        setState((s) => ({ ...s, supported: false }));
      });

    return () => {
      if (midiAccessRef.current) {
        const inputs = Array.from(midiAccessRef.current.inputs.values());
        for (const input of inputs) {
          input.onmidimessage = null;
        }
      }
    };
  }, [state.supported, handleMidiMessage]);

  return state;
}
